<template>
  <b-card>
    <div class="d-flex justify-content-between align-items-center mb-2">
      <div>
        <feather-icon icon="ServerIcon" size="18" class="mr-50" />
        <span class="font-weight-bold">TV & FTP Servers</span>
      </div>
      <b-button
        variant="primary"
        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
        @click="openModal()"
      >
        Add Link
      </b-button>
    </div>

    <b-table
      :fields="fields"
      :items="mediaServers"
      :busy="isLoading"
      small
      responsive="sm"
      show-empty
      empty-text="No server link found"
    >
      <template #table-busy>
        <div class="text-center text-muted my-2">
          <b-spinner small />
          <span class="ml-50">Loading...</span>
        </div>
      </template>

      <template #cell(type)="data">
        <b-badge :variant="data.item.type === 'tv' ? 'success' : 'info'">
          {{ data.item.type.toUpperCase() }}
        </b-badge>
      </template>

      <template #cell(url)="data">
        <a
          :href="data.item.url"
          class="text-nowrap"
          target="_blank"
          rel="noopener"
        >
          {{ data.item.url }}
        </a>
      </template>

      <template #cell(is_active)="data">
        <b-badge :variant="data.item.is_active ? 'light-success' : 'light-danger'">
          {{ data.item.is_active ? 'Active' : 'Inactive' }}
        </b-badge>
      </template>

      <template #cell(actions)="data">
        <b-button
          size="sm"
          variant="outline-primary"
          class="mr-50"
          @click="openModal(data.item)"
        >
          Edit
        </b-button>
        <b-button
          size="sm"
          variant="outline-danger"
          @click="deleteServer(data.item)"
        >
          Delete
        </b-button>
      </template>
    </b-table>

    <b-modal
      id="server-link-modal"
      :title="editItem ? 'Edit Server Link' : 'Add Server Link'"
      v-model="showModal"
      @hidden="handleModalHidden"
      hide-footer
    >
      <validation-observer ref="serverForm">
        <b-form @submit.prevent="submitForm">
          <b-row>
            <b-col cols="12" md="6">
              <validation-provider name="Type" rules="required" #default="{ errors }">
                <b-form-group label="Type" label-for="server-type">
                  <v-select
                    id="server-type"
                    :options="typeOptions"
                    :reduce="option => option.value"
                    :clearable="false"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    v-model="form.type"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>
            <b-col cols="12" md="6">
              <validation-provider name="Sort Order" rules="integer" #default="{ errors }">
                <b-form-group label="Sort Order" label-for="server-sort">
                  <b-form-input
                    id="server-sort"
                    type="number"
                    min="0"
                    v-model.number="form.sort_order"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>
            <b-col cols="12">
              <validation-provider name="Title" rules="required" #default="{ errors }">
                <b-form-group label="Title" label-for="server-title">
                  <b-form-input
                    id="server-title"
                    v-model="form.title"
                    placeholder="Enter display title"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>
            <b-col cols="12">
              <validation-provider
                name="URL"
                rules="required|url"
                #default="{ errors }"
              >
                <b-form-group label="URL" label-for="server-url">
                  <b-form-input
                    id="server-url"
                    type="url"
                    v-model="form.url"
                    placeholder="https://example.com"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>
            <b-col cols="12">
              <b-form-group label="Description" label-for="server-description">
                <b-form-textarea
                  id="server-description"
                  rows="2"
                  v-model="form.description"
                  placeholder="Optional note"
                />
              </b-form-group>
            </b-col>
            <b-col cols="12">
              <b-form-checkbox
                v-model="form.is_active"
                switch
              >
                Active
              </b-form-checkbox>
            </b-col>
          </b-row>

          <div class="d-flex justify-content-end mt-1">
            <b-button
              variant="outline-secondary"
              class="mr-50"
              @click="closeModal"
              :disabled="isSubmitting"
            >
              Cancel
            </b-button>
            <b-button
              variant="primary"
              type="submit"
              :disabled="isSubmitting"
            >
              <b-spinner small v-if="isSubmitting" class="mr-50" />
              {{ editItem ? 'Update' : 'Create' }}
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </b-modal>
  </b-card>
</template>

<script>
import {
  BBadge,
  BButton,
  BCard,
  BForm,
  BFormCheckbox,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BModal,
  BRow,
  BCol,
  BSpinner,
  BTable,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import vSelect from 'vue-select'
import useJwt from '@/auth/jwt/useJwt'
import Mixin from '@core/services/mixin'
import { checkRes } from '@core/services/helper'
import { required, url, integer } from '@validations'

export default {
  name: 'ServerLinkManager',
  directives: { Ripple },
  mixins: [Mixin],
  props: {
    ispId: {
      type: [Number, String],
      default: null,
    },
  },
  components: {
    BBadge,
    BButton,
    BCard,
    BForm,
    BFormCheckbox,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BModal,
    BRow,
    BCol,
    BSpinner,
    BTable,
    ValidationObserver,
    ValidationProvider,
    vSelect,
  },
  data() {
    return {
      mediaServers: [],
      isLoading: false,
      isSubmitting: false,
      showModal: false,
      editItem: null,
      form: this.defaultForm(),
      fields: [
        { key: 'title', label: 'Title', sortable: true },
        { key: 'type', label: 'Type', sortable: true },
        { key: 'url', label: 'URL' },
        { key: 'sort_order', label: 'Order', sortable: true, thClass: 'text-center', tdClass: 'text-center' },
        { key: 'is_active', label: 'Status', thClass: 'text-center', tdClass: 'text-center' },
        { key: 'actions', label: 'Actions', thClass: 'text-right', tdClass: 'text-right' },
      ],
      typeOptions: [
        { label: 'TV Server', value: 'tv' },
        { label: 'FTP Server', value: 'ftp' },
      ],
    }
  },
  watch: {
    ispId: {
      immediate: true,
      handler(val) {
        if (val) this.fetchServers()
      },
    },
  },
  methods: {
    defaultForm() {
      return {
        type: 'tv',
        title: '',
        url: '',
        description: '',
        sort_order: 0,
        is_active: true,
      }
    },
    fetchServers() {
      this.isLoading = true
      useJwt.getIspMediaServers({ isp_id: this.ispId })
        .then(response => {
          if (checkRes(response.status)) {
            this.mediaServers = response.data.data || []
          }
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    openModal(server = null) {
      this.editItem = server
      this.form = server ? {
        type: server.type,
        title: server.title,
        url: server.url,
        description: server.description,
        sort_order: server.sort_order,
        is_active: server.is_active,
      } : this.defaultForm()
      this.showModal = true
    },
    handleModalHidden() {
      this.showModal = false
      this.editItem = null
      this.form = this.defaultForm()
      if (this.$refs.serverForm) this.$refs.serverForm.reset()
    },
    closeModal() {
      this.showModal = false
    },
    submitForm() {
      this.$refs.serverForm.validate().then(success => {
        if (!success) return
        this.isSubmitting = true
        const payload = {
          ...this.form,
          isp_id: this.ispId,
        }

        const request = this.editItem
          ? useJwt.updateIspMediaServer(this.editItem.id, payload)
          : useJwt.createIspMediaServer(payload)

        request.then(response => {
          if (checkRes(response.status)) {
            this.toastMessage('success', 'Server Link', response)
            this.fetchServers()
            this.closeModal()
          } else {
            this.toastMessage('warning', 'Server Link', response)
          }
        }).catch(error => {
          if (error.response) {
            this.toastMessage('danger', 'Server Link', error.response)
          }
        }).finally(() => {
          this.isSubmitting = false
        })
      })
    },
    deleteServer(server) {
      this.$bvModal.msgBoxConfirm(`Delete "${server.title}"?`, {
        title: 'Delete Confirmation',
        okVariant: 'danger',
        okTitle: 'Yes, delete',
        cancelTitle: 'Cancel',
        footerClass: 'p-2',
      }).then(value => {
        if (!value) return
        useJwt.deleteIspMediaServer(server.id, { params: { isp_id: this.ispId } })
          .then(response => {
            if (checkRes(response.status)) {
              this.toastMessage('success', 'Server Link', response)
              this.fetchServers()
            } else {
              this.toastMessage('warning', 'Server Link', response)
            }
          })
          .catch(error => {
            if (error.response) {
              this.toastMessage('danger', 'Server Link', error.response)
            }
          })
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.table-responsive {
  min-height: 200px;
}
</style>
